import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { Session } from '../session';
import { SessionContext } from '../session';

import { DEFAULT_TEST_IDENTITY, createTestToken } from './createTestToken';

const STORAGE_KEY = 'pm.testLogin.token';

/**
 * Stand-in for the OIDC provider in local dev and CI. It deliberately still
 * requires an explicit sign-in action rather than auto-authenticating, so the
 * protected-route behaviour is exercised the same way it is in production.
 */
export const TestLoginSessionProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | undefined>(
    () => window.localStorage.getItem(STORAGE_KEY) ?? undefined,
  );
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (token) {
      window.localStorage.setItem(STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, [token]);

  const signIn = useCallback(() => {
    setIsLoading(true);
    createTestToken()
      .then(setToken)
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : String(cause)))
      .finally(() => setIsLoading(false));
  }, []);

  const session = useMemo<Session>(
    () => ({
      isLoading,
      isAuthenticated: Boolean(token),
      mode: 'test-login',
      ...(token
        ? {
            user: {
              displayName: DEFAULT_TEST_IDENTITY.displayName,
              subject: `${DEFAULT_TEST_IDENTITY.siteName}:${DEFAULT_TEST_IDENTITY.userId}:${DEFAULT_TEST_IDENTITY.tenantId}`,
            },
          }
        : {}),
      ...(error ? { error } : {}),
      signIn,
      signOut: () => setToken(undefined),
      // Makes the API client's 401-drop path behave the same as under OIDC:
      // a definitively rejected token clears the session to the sign-in gate.
      // Only if it is still the token held: a request sent before sign-in (the
      // sidebar's `/me`, carrying none) can answer 401 after it, and used to
      // wipe the token the click had just minted — the e2e suite's
      // intermittent "Sign in required" timeouts.
      clearSession: (rejectedToken) =>
        setToken((current) => (current === rejectedToken ? undefined : current)),
      getAccessToken: async () => token,
    }),
    [error, isLoading, signIn, token],
  );

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
};
